/**
 * @file SetUsername.jsx
 * @description Dedicated onboarding view forcing new users to select a unique username 
 *              before accessing the platform. Includes real-time debounced availability checks.
 * @module components/SetUsername
 */
import React, { useState, useEffect } from "react";
import { FiHome, FiCheck, FiX, FiArrowRight, FiCircle } from "react-icons/fi";
import { useAuth } from "../context/AuthContext";
import { publicApiPost } from '../utils/apiUtils';
import UserAvatar from './UserAvatar';

/* ══════════════════════════════════════════════════════════════
 * MAIN COMPONENT
 * ══════════════════════════════════════════════════════════════ */

/**
 * @component SetUsername
 * @description Handles the username selection and validation process for first-time sign-ins.
 *
 * @param {Object} props
 * @param {Function} props.navigate - Router navigation function.
 * @returns {JSX.Element} The rendered Set Username page.
 */

const SetUsername = ({ navigate }) => {
    // ── Component State ──
    const [username, setUsername] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const { user, accessToken, login, refreshToken } = useAuth();

    // ── Validation State ──
    const [usernameFocused, setUsernameFocused] = useState(false);
    const [usernameValidation, setUsernameValidation] = useState({
        length: false,
        format: false,
        available: null
    });

    // Set Page Title
    useEffect(() => {
        document.title = "SimpleCode | Set Your Username";
        return () => {
            document.title = "SimpleCode";
        };
    }, []);

    // Auto-clear error messages
    useEffect(() => {
        if (error) {
            const timer = setTimeout(() => setError(""), 4000);
            return () => clearTimeout(timer);
        }
    }, [error]);

    /* ══════════════════════════════════════════════════════════════
     * VALIDATION & HANDLERS
     * ══════════════════════════════════════════════════════════════ */

    /**
     * @function validateUsernameRealTime
     * @description Validates username length and format locally as the user types.
     * @param {string} value - The input username string.
     */
    const validateUsernameRealTime = (value) => {
        setUsernameValidation(prev => ({
            length: value.length >= 3 && value.length <= 20,
            format: /^[a-zA-Z0-9]+$/.test(value),
            available: prev.available
        }));
    };

    // Debounced username availability check
    useEffect(() => {
        if (!username || username.trim().length < 3 || !/^[a-zA-Z0-9]+$/.test(username)) {
            setUsernameValidation(prev => ({ ...prev, available: null }));
            return;
        }

        setUsernameValidation(prev => ({ ...prev, available: null })); // Reset to "checking"

        const timeoutId = setTimeout(async () => {
            try {
                const response = await publicApiPost('/auth/check-username', {
                    username: username.trim().toLowerCase()
                });

                if (response.ok) {
                    const data = await response.json();
                    setUsernameValidation(prev => ({ ...prev, available: data.available }));
                } else if (response.status === 409) {
                    setUsernameValidation(prev => ({ ...prev, available: false }));
                } else {
                    setUsernameValidation(prev => ({ ...prev, available: null }));
                }
            } catch (err) {
                setUsernameValidation(prev => ({ ...prev, available: null }));
            }
        }, 600);

        return () => clearTimeout(timeoutId);
    }, [username]);

    /**
     * @function isUsernameValid
     * @description Checks if all validation criteria (length, format, availability) are met.
     * @returns {boolean} True if the username is completely valid.
     */
    const isUsernameValid = () => {
        return usernameValidation.length && usernameValidation.format && usernameValidation.available === true;
    };

    /**
     * @function handleSubmit
     * @description Submits the valid username to the backend to finalize account setup.
     * @param {React.FormEvent} e 
     */
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isUsernameValid()) return;

        setLoading(true);
        setError("");

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/auth/set-username`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify({ username: username.trim().toLowerCase() })
                }
            );

            const data = await response.json();

            if (!response.ok) {
                setError(data.error || 'Failed to set username');
                setLoading(false);
                return;
            }

            // Update the user in context with the new username
            const accessTokenExpiry = localStorage.getItem('accessTokenExpiry');
            const refreshTokenExpiry = localStorage.getItem('refreshTokenExpiry');
            const storedRefreshToken = localStorage.getItem('refreshToken');

            login(
                data.user,
                accessToken,
                storedRefreshToken || refreshToken,
                accessTokenExpiry,
                refreshTokenExpiry
            );

            // Navigate to dashboard
            navigate('/dashboard');

        } catch (err) {
            setError('Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 text-foreground flex flex-col font-sans">
            {/* Nav Bar */}
            <nav className="fixed top-0 w-full bg-emerald-800 backdrop-blur-md border-b border-slate-200 z-50 px-4 sm:px-16 py-3">
                <div className="max-w-8xl mx-auto flex items-center justify-center">
                    <div className="flex items-center justify-center">
                        <button onClick={() => navigate('/')} className="focus:outline-none flex items-center justify-center">
                            <img src="/logo.png" alt="SimpleCode" className="h-10 w-auto" />
                        </button>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <div className="flex-1 flex items-center justify-center px-4 pt-20">
                <div className="w-full max-w-md animate-in fade-in slide-in-from-bottom-4 duration-700">
                    {/* Error Toast */}
                    {error && (
                        <div className="fixed bottom-5 right-5 z-50 max-w-sm w-[90%] sm:w-auto p-4 shadow-2xl bg-white border-l-4 border-red-600 animate-in slide-in-from-right duration-300">
                            <p className="text-sm font-semibold text-slate-800 flex items-center">
                                <span className="bg-red-600 text-white p-1 rounded-full mr-3"><FiX className="w-4 h-4" /></span>
                                <span className="break-words">{error}</span>
                            </p>
                        </div>
                    )}

                    {/* Heading */}
                    <div className="text-center mb-6">
                        <div className="inline-flex items-center justify-center mb-4">
                            {/* <UserAvatar avatarUrl={user?.avatar_url} size={48} /> */}
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3">
                            Claim Your <span className="text-emerald-600">Identity</span>
                        </h2>
                        <p className="text-slate-500 text-sm hidden md:block">
                            Welcome, <span className="text-emerald-600 font-bold">{user?.name || 'Explorer'}</span>! <br></br> Pick a unique username to represent you.
                        </p>
                        <p className="text-slate-500 text-xs md:hidden">
                            Welcome, <span className="text-emerald-600 font-bold">{user?.name || 'Explorer'}</span>! <br></br> Pick a unique username to represent you.
                        </p>
                    </div>

                    {/* Warning message */}
                    <div className="flex items-start justify-center bg-amber-400 p-3 rounded mb-6 mx-10 hidden md:block">
                        {/* <div className="mt-0.5 w-2 h-2 rounded-full bg-amber-600 grow-0 shrink-0"></div> */}
                        <p className="text-xs text-amber-900 font-medium leading-normal">
                            Your username is <span className="font-bold">permanent</span> and can not be changed.
                        </p>
                    </div>

                    {/* Card */}
                    <div className="bg-white rounded shadow-xl shadow-emerald-900/5 border-l-4 border-emerald-600 p-6 sm:p-10 relative overflow-hidden group">
                        {/* Decorative background element */}
                        {/* <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-emerald-400 rounded-full opacity-50 group-hover:scale-150 transition-transform duration-1000"></div> */}

                        <form onSubmit={handleSubmit} className="relative z-10 space-y-6">
                            {/* Username Input */}
                            <div className="text-left">
                                <label className="block text-sm font-semibold text-slate-700 mb-2 ml-1">
                                    Username <span className="text-red-500">*</span>
                                </label>
                                <div className="relative group">
                                    <div className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 transition-colors duration-300">
                                        <span className={`font-bold ${usernameFocused ? 'text-emerald-600' : 'text-gray-400'}`}>@</span>
                                    </div>
                                    <input
                                        type="text"
                                        placeholder="choose_your_identity"
                                        value={username}
                                        onChange={(e) => {
                                            const val = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
                                            setUsername(val);
                                            validateUsernameRealTime(val);
                                        }}
                                        onFocus={() => setUsernameFocused(true)}
                                        onBlur={() => setUsernameFocused(false)}
                                        required
                                        autoComplete="off"
                                        className="w-full pl-12 pr-4 py-3.5 bg-accent border-b-2 border-transparent rounded-t text-slate-900 placeholder-gray-400 focus:outline-none focus:ring-0 focus:border-emerald-600 transition-colors duration-200"
                                    />
                                </div>

                                {/* Validation indicators */}
                                {(
                                    <div className="mt-4 p-4 bg-gray-200 border border-border rounded space-y-3 animate-in fade-in slide-in-from-top-2 duration-300">
                                        <p className="text-[12px] uppercase tracking-widest text-slate-700 font-bold mb-1">Requirements</p>
                                        <div className="grid grid-cols-1 gap-2">
                                            {[
                                                { key: 'length', condition: usernameValidation.length, text: '3-20 Characters' },
                                                { key: 'format', condition: usernameValidation.format, text: 'Letters & Numbers Only' }
                                            ].map(({ key, condition, text }) => (
                                                <div key={key} className="flex items-center space-x-2">
                                                    <div className={`w-4 h-4 rounded-full flex items-center justify-center transition-all ${condition ? 'text-emerald-700' : 'text-slate-700'}`}>
                                                        {condition ? <FiCheck className="w-4 h-4" /> : <FiCircle className="w-3 h-3" />}
                                                    </div>
                                                    <span className={`text-xs font-medium transition-colors ${condition ? 'text-emerald-700' : 'text-slate-700'}`}>{text}</span>
                                                </div>
                                            ))}

                                            {/* Availability check */}
                                            {username.length >= 3 && usernameValidation.format && (
                                                <div className="flex items-center space-x-2 pt-1 mt-1 border-t border-slate-200/50">
                                                    <div className={`w-5 h-5 rounded-full flex items-center justify-center transition-all ${usernameValidation.available === true ? 'bg-emerald-700 text-white' :
                                                        usernameValidation.available === false ? 'bg-red-700 text-white' : 'text-amber-700'
                                                        }`}>
                                                        {usernameValidation.available === true && <FiCheck className="w-3 h-3" />}
                                                        {usernameValidation.available === false && <FiX className="w-3 h-3" />}
                                                        {usernameValidation.available === null && <div className="w-3 h-3 border-2 border-amber-600 border-t-transparent rounded-full animate-spin"></div>}
                                                    </div>
                                                    <span className={`text-sm font-semibold transition-colors ${usernameValidation.available === true ? 'text-emerald-700' :
                                                        usernameValidation.available === false ? 'text-red-700' : 'text-amber-600'
                                                        }`}>
                                                        {usernameValidation.available === true ? 'Available' :
                                                            usernameValidation.available === false ? 'Already taken' :
                                                                'Checking...'}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Submit Button */}
                            <button
                                type="submit"
                                disabled={loading || !isUsernameValid()}
                                className="group relative w-full py-4 bg-emerald-600 text-white font-semibold rounded transition-all duration-300 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none flex items-center justify-center overflow-hidden"
                            >
                                {/* <div className="absolute inset-0 w-1/2 h-full -skew-x-12 -translate-x-full group-hover:translate-x-[250%] transition-transform duration-1000 ease-in-out"></div> */}
                                {loading ? (
                                    <div className="animate-spin rounded-full h-6 w-6 border-3 border-white border-t-transparent"></div>
                                ) : (
                                    <>
                                        <span>Confirm Username</span>
                                        {/* <FiArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" /> */}
                                    </>
                                )}
                            </button>
                        </form>
                    </div>

                    {/* ── Compliance Footer ── */}
                    <div className="my-8 text-center text-xs text-muted-foreground px-4">
                        <p className="leading-relaxed">
                            By continuing, you agree to SimpleCode's{" "} <br></br>
                            <button
                                onClick={() => navigate('/terms-of-service')}
                                className="text-emerald-600 font-semibold hover:text-emerald-700 transition-colors hover:underline"
                            >
                                Terms of Service
                            </button>{" "}
                            and{" "}
                            <button
                                onClick={() => navigate('/privacy-policy')}
                                className="text-emerald-600 font-semibold hover:text-emerald-700 transition-colors hover:underline"
                            >
                                Privacy Policy
                            </button>
                        </p>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default SetUsername;
